import React, { useEffect, useState } from 'react';
import { Api } from '@/services/Api';
import { useStore } from '@/components/store/Store';
import { Employee } from '@/declarations/models/Employee';
import { useCurrentBlock } from '../CurrentBlockProvider';
import { BylineBlockModel } from '../../../declarations/models/blocks/BylineBlock';
import { BlockPreviewComponent } from '../../lib/declarations/EditorComponentTypes';

const isLinked = (employee: Employee) => !!(employee.linked_page?.url || employee.link_list?.[0]?.url_external);

export const BylineBlockPreview: BlockPreviewComponent = () => {
  const { block } = useCurrentBlock();
  const currentBlock = block as BylineBlockModel;
  const { state } = useStore();
  const siteId = state.selectedSite?.id || 0;
  const [employees, setEmployees] = useState<Array<Employee>>([]);

  let mode = 'person';

  if (currentBlock.mode) {
    mode = currentBlock.mode;
  } else if (currentBlock?.author?.id) {
    mode = 'person';
  } else {
    mode = 'manual';
  }

  useEffect(() => {
    const ids = (currentBlock?.employees || []).map((e) => e.id).filter((id): id is number => !!id);
    if (ids.length === 0) {
      setEmployees([]);
      return undefined;
    }

    // Fetched one by one, as the editor list does, and kept in block order.
    // An employee that fails to load is left out rather than hiding the rest.
    let cancelled = false;
    const contexts = ids.map((id) => Api.getEmployeeSimple(id));
    Promise.all(
      contexts.map((ctx) =>
        ctx.fetchDirect(null).catch((error) => {
          console.error(error);
          return null;
        }),
      ),
    ).then((responses) => {
      if (!cancelled) setEmployees(responses.filter((e): e is Employee => !!e));
    });

    return () => {
      cancelled = true;
      contexts.forEach((ctx) => ctx.abort());
    };
  }, [currentBlock?.employees, siteId]);

  // One grid per employee. The leadtext belongs to the block, not to each
  // author, so with several it is placed once above the grids. A single
  // employee keeps it inside the content, as before.
  if (mode === 'employee') {
    const several = employees.length > 1;
    return (
      <div className='article__grid'>
        <div className='module article__byline'>
          {several && currentBlock.leadtext && <div className='byline__leadtext'>{currentBlock.leadtext}</div>}
          {employees.map((employee) => (
            <div className='module__grid' key={employee.id}>
              <div className='module__media'>
                {employee.resource?.url && <img src={employee.resource.url} alt='' />}
              </div>
              <div className='module__content'>
                {!several && <div className='byline__leadtext'>{currentBlock.leadtext}</div>}
                <div className='byline__author'>
                  <span style={isLinked(employee) ? { textDecoration: 'underline' } : undefined}>
                    {employee.first_name} {employee.last_name}
                  </span>
                </div>
                <div className='byline__descr'>{employee.position || ''}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className='article__grid'>
      <div className='module article__byline'>
        <div className='module__grid'>
          <div className='module__media'>
            {mode === 'manual' && currentBlock?.mediaobject && <img src={currentBlock?.mediaobject?.url} alt='' />}
            {mode === 'person' && <img src={currentBlock?.author?.content?.mediaobject?.url} alt='' />}
          </div>
          <div className='module__content'>
            <div className='byline__leadtext'>{currentBlock.leadtext}</div>
            <div className='byline__author'>
              {mode === 'manual' && currentBlock?.authortext && currentBlock.authortext}
              {mode === 'person' && currentBlock?.author?.id && `${currentBlock?.author?.title}`}
            </div>
            <div className='byline__descr'>
              {mode === 'manual' && currentBlock?.description}
              {mode === 'person' && currentBlock?.author?.id && `${currentBlock?.author?.content?.descr}`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BylineBlockPreview;
